import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { fmtDbDate, fmtDbDateTime, localYMD } from '../utils/dbTime';
import { billableStops, isDriverStop, driverStopTag } from '../utils/stopKinds';
import { ARCHIVE_LIST_ORDER, archiveList, routeLabelOf, jobPriceOf, driverNameOf } from '../utils/archiveLists';
import DelayBadge from './DelayBadge';   // __DELAY_VIEW__ billable delay badge (office logins)
import { 
  getDefaultUnitSystem, 
  formatLength, 
  formatWeight, 
  formatArea, 
  formatVolume,
  getLengthUnit,
  getWeightUnit,
  getAreaUnit,
  getVolumeUnit
} from '../utils/units';

// Terminal statuses — the Archive's domain (operational statuses live on the
// Kanban board). Kept explicit so the two surfaces never overlap.
// __RTB_RETIRED__ 'ready_to_bill' is retired but stays in this list so an old row
// still shows here (as complete) instead of vanishing.
const TERMINAL_STATUSES = ['complete', 'ready_to_bill', 'billed', 'archived', 'cancelled'];   // __STATUS_TRUTH__ + the Cancelled list

// Rows per page. The archive is paginated (load-more) and NEVER fetched
// wholesale, so it can't hit the 1000-row cap regardless of history size.
const PAGE_SIZE = 100;

const SELECT_COLS = `
  *,
  customer:customers(id, company_name, phone),
  driver:drivers(id, name, user:user_id(name)),
  truck:trucks(truck_number, license_plate),
  route_loads(sequence_order, routes(route_number)),
  load_stops(id, stop_type, stop_sequence, address, time_window_start, time_window_end, actual_arrival, actual_departure, notes)
`;

function CompletedJobsArchive({ onCloneJob }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterDriver, setFilterDriver] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [customers, setCustomers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [expandedJob, setExpandedJob] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [archiveMsg, setArchiveMsg] = useState(null); // { type, text }
  const [cloning, setCloning] = useState(null);
  const [stats, setStats] = useState(null);
  const [sortBy, setSortBy] = useState('completed_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  // __BILLED_LIST__ which list: Completed (to bill) / Billed + Archived / All finished
  const [listKey, setListKey] = useState('to_bill');

  // Debounce the search box so each keystroke doesn't fire a server query.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Apply the current filters + server-side search to a query builder. Search is
  // an ilike OR across base-table text columns (load_number, commodity,
  // customer_name, booker_name, notes) so it hits the FULL table, not just the
  // rows already loaded on screen. The term is sanitized of characters that
  // would break PostgREST's or() grammar.
  const applyFilters = useCallback((q) => {
    // __BILLED_LIST__ the chosen list's statuses; its own day (billed_at on Billed / Archived)
    const lst = archiveList(listKey);
    q = q.in('status', lst.statuses);
    if (dateFrom) q = q.gte(lst.dateCol, dateFrom);
    if (dateTo) q = q.lte(lst.dateCol, dateTo + 'T23:59:59');
    if (filterCustomer) q = q.eq('customer_id', filterCustomer);
    if (filterDriver) q = q.eq('driver_id', filterDriver);
    const term = debouncedSearch.trim().replace(/[%,()]/g, ' ').trim();
    if (term) {
      q = q.or(
        `load_number.ilike.%${term}%,commodity.ilike.%${term}%,` +
        `customer_name.ilike.%${term}%,booker_name.ilike.%${term}%,notes.ilike.%${term}%`
      );
    }
    return q;
  }, [listKey, dateFrom, dateTo, filterCustomer, filterDriver, debouncedSearch]);

  const fetchJobs = useCallback(async (pageToLoad, append) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const from = pageToLoad * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const { data, error } = await applyFilters(supabase.from('loads').select(SELECT_COLS))
        .order(sortBy, { ascending: sortOrder === 'asc', nullsFirst: false })
        .range(from, to);
      if (error) throw error;

      const rows = data || [];
      setJobs(prev => (append ? [...prev, ...rows] : rows));
      setHasMore(rows.length === PAGE_SIZE);

      // On a fresh load (not append), get the accurate full count for the
      // current filter/search via a head count — no rows transferred.
      if (!append) {
        const { count, error: countErr } = await applyFilters(
          supabase.from('loads').select('id', { count: 'exact', head: true })
        );
        if (countErr) throw countErr;
        setTotalCount(count || 0);
      }
    } catch (error) {
      console.error('Error fetching completed jobs:', error);
    } finally {
      if (append) setLoadingMore(false); else setLoading(false);
    }
  }, [applyFilters, sortBy, sortOrder]);

  const fetchFilters = async () => {
    const [customersResult, driversResult] = await Promise.all([
      supabase.from('customers').select('id, company_name').order('company_name'),
      supabase.from('drivers').select('id, name, email, user:user_id(name)').eq('is_available', true)
    ]);

    if (customersResult.data) setCustomers(customersResult.data);
    if (driversResult.data) setDrivers(driversResult.data);
  };

  // Any filter/search/sort change resets to the first page and refetches.
  useEffect(() => {
    setPage(0);
    fetchJobs(0, false);
  }, [fetchJobs]);

  useEffect(() => {
    fetchFilters();
  }, []);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchJobs(next, true);
  };

  // Aggregates: Total Jobs is the accurate server count for the current
  // filter/search; the money/miles/hours/unique aggregates are computed over the
  // rows actually loaded so far (they grow as you Load More).
  useEffect(() => {
    const totalRevenue = jobs.reduce((sum, job) => sum + (jobPriceOf(job) || 0), 0);
    const totalMiles = jobs.reduce((sum, job) => sum + (parseFloat(job.total_distance_km) || 0), 0) * 0.621371;
    const totalHours = jobs.reduce((sum, job) => sum + (parseFloat(job.estimated_duration_hours) || 0), 0);
    const uniqueCustomers = new Set(jobs.map(j => j.customer_id).filter(Boolean)).size;
    const uniqueDrivers = new Set(jobs.map(j => j.driver_id).filter(Boolean)).size;

    setStats({
      totalJobs: totalCount,
      totalRevenue,
      totalMiles: Math.round(totalMiles),
      totalHours: Math.round(totalHours * 10) / 10,
      uniqueCustomers,
      uniqueDrivers,
      avgRevenuePerJob: jobs.length > 0 ? Math.round(totalRevenue / jobs.length) : 0
    });
  }, [jobs, totalCount]);

  // __BILLED_LIST__ switching list sorts by that list's day, newest first
  const chooseList = (key) => {
    setListKey(key);
    setSortBy(archiveList(key).dateCol);
    setSortOrder('desc');
    setSelectedJobs([]);
  };

  const handleSelectJob = (jobId) => {
    setSelectedJobs(prev => {
      if (prev.includes(jobId)) {
        return prev.filter(id => id !== jobId);
      } else {
        return [...prev, jobId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedJobs.length === jobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(jobs.map(j => j.id));
    }
  };

  const handleCloneJob = async (job) => {
    setCloning(job.id);
    try {
      const newJob = {
        load_number: `${job.load_number || 'LOAD'}-CLONE-${Date.now().toString().slice(-4)}`,
        customer_id: job.customer_id,
        commodity: job.commodity,
        weight_kg: job.weight_kg,
        length_cm: job.length_cm,
        width_cm: job.width_cm,
        height_cm: job.height_cm,
        rate: job.rate,
        notes: job.notes,
        booker_name: job.booker_name,
        status: 'pending',
        created_at: new Date().toISOString()
      };

      const { data: createdJob, error: jobError } = await supabase
        .from('loads')
        .insert(newJob)
        .select()
        .single();

      if (jobError) throw jobError;

      // __DRIVER_STOP_UNBILLED__ a clone is a new customer job: never the driver's fuel/break stops.
      const cloneStops = billableStops(job.load_stops);
      if (cloneStops.length > 0) {
        const newStops = cloneStops.map(stop => ({
          load_id: createdJob.id,
          stop_type: stop.stop_type,
          stop_sequence: stop.stop_sequence,
          address: stop.address,
          notes: stop.notes
        }));

        const { error: stopsError } = await supabase
          .from('load_stops')
          .insert(newStops);

        if (stopsError) throw stopsError;
      }

      setArchiveMsg({ type: 'success', text: `Cloned → ${createdJob.load_number}` });
      
      if (onCloneJob) {
        onCloneJob(createdJob);
      }
    } catch (error) {
      console.error('Error cloning job:', error);
      setArchiveMsg({ type: 'error', text: 'Clone failed: ' + error.message });
    } finally {
      setCloning(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      // Exports the selected rows, or all currently-loaded rows (Load More to
      // include older ones). For a full-history export, use the date filters.
      const jobsToExport = selectedJobs.length > 0
        ? jobs.filter(j => selectedJobs.includes(j.id))
        : jobs;

      if (jobsToExport.length === 0) {
        setArchiveMsg({ type: 'error', text: 'No jobs to export' });
        setExporting(false);
        return;
      }

      const unitSystem = getDefaultUnitSystem();
      const lengthUnit = getLengthUnit(unitSystem);
      const weightUnit = getWeightUnit(unitSystem);
      const areaUnit = getAreaUnit(unitSystem);
      const volumeUnit = getVolumeUnit(unitSystem);

      // __DRIVER_STOP_UNBILLED__ the export is the customer's stops (it is used for billing);
      // a driver's fuel/break stop stays in the archive view, not in Stop N Address.
      const maxStops = Math.max(...jobsToExport.map(job => {
        const allStops = billableStops(job.load_stops).sort((a, b) => {
          if (a.stop_type !== b.stop_type) return a.stop_type === 'pickup' ? -1 : 1;
          return a.stop_sequence - b.stop_sequence;
        });
        return allStops.length;
      }), 1);

      const stopHeaders = [];
      for (let i = 1; i <= maxStops; i++) {
        stopHeaders.push(`Stop ${i} Address`);
      }

      const headers = [
        'Load Number',
        'Status',
        'Completed Date',
        'Billed Date',
        'Route',
        ...stopHeaders,
        `Weight (${weightUnit})`,
        `Length (${lengthUnit})`,
        `Width (${lengthUnit})`,
        `Height (${lengthUnit})`,
        `Area (${areaUnit})`,
        `Volume (${volumeUnit})`,
        'Customer Name',
        'Booker Name',
        'Driver Name',
        'Rate',
        'Commodity',
        'Notes'
      ];

      const rows = jobsToExport.map(job => {
        const allStops = billableStops(job.load_stops).sort((a, b) => {
          if (a.stop_type !== b.stop_type) return a.stop_type === 'pickup' ? -1 : 1;
          return a.stop_sequence - b.stop_sequence;
        });

        const stopAddresses = [];
        for (let i = 0; i < maxStops; i++) {
          stopAddresses.push(allStops[i]?.address || '');
        }

        const weight = formatWeight(job.weight_kg, unitSystem);
        const length = formatLength(job.length_cm, unitSystem);
        const width = formatLength(job.width_cm, unitSystem);
        const height = formatLength(job.height_cm, unitSystem);
        const area = formatArea(job.length_cm, job.width_cm, unitSystem);
        const volume = formatVolume(job.length_cm, job.width_cm, job.height_cm, unitSystem);

        return [
          job.load_number || '',
          job.status || '',
          job.completed_at ? fmtDbDate(job.completed_at) : '',
          job.billed_at ? fmtDbDate(job.billed_at) : '',
          routeLabelOf(job),
          ...stopAddresses,
          weight,
          length,
          width,
          height,
          area,
          volume,
          job.customer?.company_name || job.customer_name || '',
          job.booker_name || '',
          driverNameOf(job),
          jobPriceOf(job) || '',
          job.commodity || '',
          (job.notes || '').replace(/"/g, '""')
        ];
      });

      const escapeCSV = (value) => {
        const str = String(value);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(escapeCSV).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const dateRange = dateFrom && dateTo 
        ? `_${dateFrom}_to_${dateTo}` 
        : dateFrom 
          ? `_from_${dateFrom}` 
          : dateTo 
            ? `_to_${dateTo}` 
            : '';
      
      link.download = `completed_jobs${dateRange}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setArchiveMsg({ type: 'success', text: `Exported ${jobsToExport.length} jobs` });
    } catch (error) {
      console.error('Error exporting jobs:', error);
      setArchiveMsg({ type: 'error', text: 'Export failed: ' + error.message });
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterCustomer('');
    setFilterDriver('');
    setDateFrom('');
    setDateTo('');
  };

  const setQuickDateRange = (range) => {
    const today = new Date();
    let from, to;

    switch (range) {
      case 'today':
        from = to = localYMD(today);
        break;
      case 'week':
        from = localYMD(new Date(today.setDate(today.getDate() - 7)));
        to = localYMD();
        break;
      case 'month':
        from = localYMD(new Date(today.setMonth(today.getMonth() - 1)));
        to = localYMD();
        break;
      case 'quarter':
        from = localYMD(new Date(today.setMonth(today.getMonth() - 3)));
        to = localYMD();
        break;
      case 'year':
        from = localYMD(new Date(today.setFullYear(today.getFullYear() - 1)));
        to = localYMD();
        break;
      default:
        from = '';
        to = '';
    }

    setDateFrom(from);
    setDateTo(to);
  };

  return (
    <div style={{ 
      backgroundColor: 'var(--bg-primary)', 
      borderRadius: '10px', 
      padding: '1.5rem',
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {archiveMsg && (
        <div style={{ padding: '0.6rem 1rem', marginBottom: '0.75rem', borderRadius: '5px', fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between',
          backgroundColor: archiveMsg.type === 'success' ? 'var(--color-success)' : 'var(--color-danger)', color: 'var(--text-primary)' }}>
          <span>{archiveMsg.text}</span>
          <button onClick={() => setArchiveMsg(null)} style={{ background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer' }}>✕</button>
        </div>
      )}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <h2 style={{ margin: 0, color: 'var(--accent-primary)' }}>Jobs Archive</h2>
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
          {ARCHIVE_LIST_ORDER.map((k) => (
            <button
              key={k}
              onClick={() => chooseList(k)}
              style={{
                padding: '0.4rem 0.8rem',
                borderRadius: '5px',
                border: '1px solid var(--border-default)',
                backgroundColor: listKey === k ? 'var(--accent-primary)' : 'var(--surface-1)',
                color: listKey === k ? '#000' : 'var(--text-primary)',
                fontWeight: listKey === k ? 'bold' : 'normal',
                cursor: 'pointer'
              }}
            >
              {archiveList(k).label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={handleExport}
            disabled={exporting}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: 'var(--accent-blue)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '5px',
              cursor: exporting ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              opacity: exporting ? 0.7 : 1
            }}
          >
            {exporting ? 'Exporting...' : `Export ${selectedJobs.length > 0 ? `(${selectedJobs.length})` : 'All'}`}
          </button>
        </div>
      </div>

      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '0.75rem',
          marginBottom: '1rem',
          padding: '1rem',
          backgroundColor: 'var(--surface-1)',
          borderRadius: '8px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--accent-primary)' }}>{stats.totalJobs}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Total Jobs</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--accent-blue)' }}>${stats.totalRevenue.toLocaleString()}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Revenue</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-warning)' }}>{stats.totalMiles.toLocaleString()}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Miles</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-info)' }}>{stats.totalHours}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Hours</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-danger)' }}>{stats.uniqueCustomers}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Customers</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-success)' }}>{stats.uniqueDrivers}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Drivers</div>
          </div>
        </div>
      )}

      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.75rem',
        marginBottom: '1rem',
        padding: '1rem',
        backgroundColor: 'var(--surface-1)',
        borderRadius: '8px'
      }}>
        <div style={{ flex: '1 1 200px' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Search</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search jobs..."
            style={{
              width: '100%',
              padding: '0.5rem',
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--border-default)',
              borderRadius: '5px',
              color: 'var(--text-primary)'
            }}
          />
        </div>

        <div style={{ flex: '1 1 150px' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer</label>
          <select
            value={filterCustomer}
            onChange={(e) => setFilterCustomer(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--border-default)',
              borderRadius: '5px',
              color: 'var(--text-primary)'
            }}
          >
            <option value="">All Customers</option>
            {customers.map(c => (
              <option key={c.id} value={c.id}>{c.company_name}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: '1 1 150px' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Driver</label>
          <select
            value={filterDriver}
            onChange={(e) => setFilterDriver(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--border-default)',
              borderRadius: '5px',
              color: 'var(--text-primary)'
            }}
          >
            <option value="">All Drivers</option>
            {drivers.map(d => (
              <option key={d.id} value={d.id}>{d.name || d.user?.name || d.email || 'Unknown'}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: '1 1 130px' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>From Date</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--border-default)',
              borderRadius: '5px',
              color: 'var(--text-primary)'
            }}
          />
        </div>

        <div style={{ flex: '1 1 130px' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>To Date</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--border-default)',
              borderRadius: '5px',
              color: 'var(--text-primary)'
            }}
          />
        </div>

        <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-end', gap: '0.25rem' }}>
          <button onClick={() => setQuickDateRange('week')} style={quickDateBtnStyle}>Week</button>
          <button onClick={() => setQuickDateRange('month')} style={quickDateBtnStyle}>Month</button>
          <button onClick={() => setQuickDateRange('quarter')} style={quickDateBtnStyle}>Quarter</button>
          <button onClick={() => setQuickDateRange('year')} style={quickDateBtnStyle}>Year</button>
          <button onClick={clearFilters} style={{ ...quickDateBtnStyle, backgroundColor: '#7f8c8d' }}>Clear</button>
        </div>
      </div>

      <div style={{ 
        flex: 1, 
        overflow: 'auto',
        backgroundColor: 'var(--surface-1)',
        borderRadius: '8px'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
            Loading {archiveList(listKey).label.toLowerCase()}...
          </div>
        ) : jobs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
            <p>No jobs found</p>
            <p style={{ fontSize: '0.85rem' }}>Try adjusting your filters or date range</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-primary)', position: 'sticky', top: 0 }}>
                <th style={thStyle}>
                  <input
                    type="checkbox"
                    checked={selectedJobs.length === jobs.length && jobs.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th style={thStyle} onClick={() => handleSort('load_number')}>
                  Load # {sortBy === 'load_number' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th style={thStyle}>Status</th>
                <th style={thStyle} onClick={() => handleSort('completed_at')}>
                  Completed {sortBy === 'completed_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th style={thStyle} onClick={() => handleSort('billed_at')}>
                  Billed {sortBy === 'billed_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th style={thStyle}>Customer</th>
                <th style={thStyle}>Driver</th>
                <th style={thStyle}>Route</th>
                <th style={thStyle}>Commodity</th>
                <th style={thStyle} onClick={() => handleSort('final_price')}>
                  Rate {sortBy === 'final_price' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => {
                const stops = (job.load_stops || []).sort((a, b) => {
                  if (a.stop_type !== b.stop_type) return a.stop_type === 'pickup' ? -1 : 1;
                  return a.stop_sequence - b.stop_sequence;
                });
                const firstPickup = stops.find(s => s.stop_type === 'pickup');
                const lastDelivery = [...stops].reverse().find(s => s.stop_type === 'delivery');

                return (
                  <React.Fragment key={job.id}>
                    <tr 
                      style={{ 
                        backgroundColor: selectedJobs.includes(job.id) ? 'rgba(22, 199, 154, 0.1)' : 'transparent',
                        cursor: 'pointer'
                      }}
                      onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                    >
                      <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedJobs.includes(job.id)}
                          onChange={() => handleSelectJob(job.id)}
                        />
                      </td>
                      <td style={tdStyle}>
                        <span style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>
                          {job.load_number || job.id.slice(0, 8)}
                        </span>
                        <DelayBadge loadId={job.id} style={{ marginLeft: '0.4rem' }} />
                      </td>
                      <td style={tdStyle}>
                        <span style={{ fontSize: '0.75rem', padding: '0.1rem 0.4rem', borderRadius: '3px', border: '1px solid var(--border-default)',
                          color: job.status === 'billed' || job.status === 'archived' ? 'var(--accent-blue)' : 'var(--color-success)' }}>
                          {job.status === 'ready_to_bill' ? 'complete' : (job.status || '-')}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        {job.completed_at ? fmtDbDate(job.completed_at) : '-'}
                      </td>
                      <td style={tdStyle} title={job.invoice_number ? 'Invoice ' + job.invoice_number : ''}>
                        {job.billed_at ? fmtDbDate(job.billed_at) : '-'}
                        {job.invoice_number && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>#{job.invoice_number}</div>}
                      </td>
                      <td style={tdStyle}>{job.customer?.company_name || job.customer_name || '-'}</td>
                      <td style={tdStyle}>{driverNameOf(job) || '-'}</td>
                      <td style={{ ...tdStyle, maxWidth: '220px' }}>
                        {routeLabelOf(job) && (
                          <div style={{ fontSize: '0.7rem', color: 'var(--accent-blue)', whiteSpace: 'nowrap' }} title="The route this job was driven on (its place in the route)">
                            {routeLabelOf(job)}
                          </div>
                        )}
                        <div style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {firstPickup?.address?.split(',')[0] || 'N/A'} → {lastDelivery?.address?.split(',')[0] || 'N/A'}
                        </div>
                      </td>
                      <td style={tdStyle}>{job.commodity || '-'}</td>
                      <td style={tdStyle}>{jobPriceOf(job) ? `$${jobPriceOf(job)}` : '-'}</td>
                      <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleCloneJob(job)}
                          disabled={cloning === job.id}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: 'var(--color-warning)',
                            color: '#000',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: cloning === job.id ? 'not-allowed' : 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {cloning === job.id ? '...' : 'Clone'}
                        </button>
                      </td>
                    </tr>
                    {expandedJob === job.id && (
                      <tr>
                        <td colSpan="11" style={{ padding: '1rem', backgroundColor: 'var(--bg-primary)' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                            <div>
                              <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--accent-primary)' }}>Job Details</h4>
                              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                <div>Weight: {job.weight_kg ? `${job.weight_kg} kg` : 'N/A'}</div>
                                <div>Dimensions: {job.length_cm && job.width_cm && job.height_cm 
                                  ? `${job.length_cm} x ${job.width_cm} x ${job.height_cm} cm` 
                                  : 'N/A'}</div>
                                <div>Booker: {job.booker_name || 'N/A'}</div>
                                <div>Notes: {job.notes || 'None'}</div>
                              </div>
                            </div>
                            <div>
                              <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--accent-primary)' }}>Stops ({stops.length})</h4>
                              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                {stops.map((stop, i) => (
                                  <div key={stop.id} style={{ marginBottom: '0.25rem' }}>
                                    <span style={{ 
                                      color: isDriverStop(stop) ? 'var(--text-secondary)' : stop.stop_type === 'pickup' ? 'var(--accent-blue)' : 'var(--color-danger)',
                                      fontWeight: 'bold'
                                    }}>
                                      {i + 1}. {isDriverStop(stop) ? driverStopTag(stop) : stop.stop_type === 'pickup' ? 'P' : 'D'}:
                                    </span> {stop.address}{isDriverStop(stop) && <em style={{ opacity: 0.7 }}> (driver {stop.stop_type} stop, not billed)</em>}
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div>
                              <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--accent-primary)' }}>Timestamps</h4>
                              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                <div>Created: {job.created_at ? fmtDbDateTime(job.created_at) : 'N/A'}</div>
                                <div>Completed: {job.completed_at ? fmtDbDateTime(job.completed_at) : 'N/A'}</div>
                                <div>Billed: {job.billed_at ? fmtDbDateTime(job.billed_at) : '-'}{job.invoice_number ? ' (invoice ' + job.invoice_number + ')' : ''}</div>
                                {job.archived_at && <div>Archived: {fmtDbDateTime(job.archived_at)}</div>}
                                {routeLabelOf(job) && <div>Route: {routeLabelOf(job)}</div>}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {hasMore && !loading && (
          <div style={{ textAlign: 'center', padding: '1rem' }}>
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: 'var(--accent-blue)',
                color: 'var(--text-primary)',
                border: 'none',
                borderRadius: '5px',
                cursor: loadingMore ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                opacity: loadingMore ? 0.7 : 1
              }}
            >
              {loadingMore ? 'Loading…' : `Load more (${jobs.length} of ${totalCount})`}
            </button>
          </div>
        )}
      </div>

      <div style={{
        marginTop: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: 'var(--text-muted)',
        fontSize: '0.85rem'
      }}>
        <span>
          Showing {jobs.length} of {totalCount} jobs ({archiveList(listKey).label})
          {selectedJobs.length > 0 && ` (${selectedJobs.length} selected)`}
        </span>
        <span>
          {dateFrom && dateTo ? `Date range: ${dateFrom} to ${dateTo}` : 
           dateFrom ? `From: ${dateFrom}` : 
           dateTo ? `To: ${dateTo}` : 'All time'}
        </span>
      </div>
    </div>
  );
}

const thStyle = {
  padding: '0.75rem',
  textAlign: 'left',
  color: 'var(--text-muted)',
  fontSize: '0.8rem',
  fontWeight: 'bold',
  borderBottom: '1px solid var(--border-default)',
  cursor: 'pointer',
  userSelect: 'none'
};

const tdStyle = {
  padding: '0.75rem',
  borderBottom: '1px solid var(--border-default)',
  color: 'var(--text-secondary)',
  fontSize: '0.85rem'
};

const quickDateBtnStyle = {
  padding: '0.5rem 0.75rem',
  backgroundColor: '#2d3748',
  color: '#fff',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer',
  fontSize: '0.75rem'
};

export default CompletedJobsArchive;
